import { useNavigate, useSearchParams } from 'react-router-dom';
import useQuotes from '~/hooks/use-quotes';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Quote, QuoteParcel } from '~/lib/types/basket-types';
import {} from '@parcel2go/npm-p2g-app';
import { useAnalytics, useTenant } from '@parcel2go/npm-p2g-app';
import Quotes from '~/components/quote-results/quotes';
import { P2GContainer } from '@parcel2go/core-ui';
import { generateLegacyQuoteHref, quoteRequestParams, quoteToAnalyticsEvent } from '~/lib/quotes';
import { CmsPromotion } from '~/components/quote-results/promotions/quote-promotions';
import { buildQuoteUrl } from '~/lib/utils/parse-parcels-url-string';
import { PostcodePrompt } from '~/components/quote-results/search/postcode-prompt';
import { useCmsContent } from '~/hooks/use-cms-content';
import { ShopSearchProvider } from '~/contexts/shop-search-context';

export function Component() {
  const { settings } = useTenant();
  const { track } = useAnalytics();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [showPostcode, setShowPostcode] = useState(false);
  const [postcode, setPostcode] = useState<string>();

  const options = useMemo(() => {
    return quoteRequestParams(searchParams, settings);
  }, [searchParams, settings]);

  useCmsContent(options);

  const geoPostcode = useMemo(() => {
    if (postcode) {
      return postcode;
    }
    if (options.originPostcode) {
      return options.originPostcode;
    }
  }, [options, postcode]);

  const onChange = useCallback(
    (
      origin: string,
      destination: string,
      parcels: QuoteParcel[],
      originPostcode?: string,
      destinationPostcode?: string
    ) => {
      navigate(buildQuoteUrl(origin, originPostcode, destination, destinationPostcode, parcels));
    },
    [navigate]
  );

  const onGeoError = useCallback(() => {
    setShowPostcode(true);
  }, []);

  useEffect(() => {
    track({ type: 'quote_view', payload: options });
    document.title = 'Quote Results';
  }, []);

  const { quotes, loading, id } = useQuotes(options);

  const onQuoteClick = useCallback(
    (index: number, quote: Quote, extras: string[]) => {
      const params = generateLegacyQuoteHref(
        options.origin,
        options.destination,
        options.originPostcode,
        options.destinationPostcode,
        options.parcels,
        quote,
        extras
      );

      try {
        track({
          type: 'select_item',
          payload: {
            item_list_name: id,
            item_list_id: id,
            items: [{ index: index, ...quoteToAnalyticsEvent(quote, extras) }]
          }
        });
      } catch (e) {
        console.error('Error tracking select_item', e);
      }

      navigate(`../../order-process/new?${params}`, { relative: 'path' });
    },
    [id, navigate, options, track]
  );

  const onPromotionClick = useCallback(
    (index: number, promotion: CmsPromotion, extras: string[]) => {
      if (!promotion.quote) {
        return;
      }

      const params = generateLegacyQuoteHref(
        options.origin,
        options.destination,
        options.originPostcode,
        options.destinationPostcode,
        options.parcels,
        promotion.quote,
        extras
      );

      try {
        track({
          type: 'select_promotion',
          payload: {
            promotion_name: promotion.title,
            items: [
              { index: index, creative_name: promotion.courierName, ...quoteToAnalyticsEvent(promotion.quote, extras) }
            ]
          }
        });
      } catch (e) {
        console.error('Error tracking select_item', e);
      }

      navigate(`../../order-process/new?${params}`, { relative: 'path' });
    },
    [navigate, options, track]
  );

  return (
    <P2GContainer maxWidth="lg">
      <ShopSearchProvider>
        <Quotes
          key={id}
          quoteId={id}
          loading={loading}
          quotes={quotes}
          postcode={geoPostcode}
          onSelect={onQuoteClick}
          origin={options.origin}
          originPostcode={options.originPostcode}
          destination={options.destination}
          destinationPostcode={options.destinationPostcode}
          parcels={options.parcels}
          onChange={onChange}
          onGeoError={onGeoError}
          onPromotionSelect={onPromotionClick}
          serviceFilter={options.filter}
          enforcedVatMode={options.enforcedVatMode}
        />
      </ShopSearchProvider>
      <PostcodePrompt
        dataTestId="geo-postcode-prompt"
        postcode={postcode}
        onSubmit={(p) => {
          setPostcode(p);
          setShowPostcode(false);
        }}
        onCancel={() => {
          setShowPostcode(false);
        }}
        showPostcode={showPostcode}
      />
    </P2GContainer>
  );
}

Component.displayName = 'Quote Checkout Step';
